import { useCallback } from 'react';
import { useChatStore } from '../store/chatStore';
import { api } from '../services/api';

/** Parameters accepted by {@link useFeedback.submitFeedback}. */
interface FeedbackParams {
  /** ID of the assistant message being rated. */
  messageId: string;
  /** The original user query that produced the response. */
  query: string;
  /** Whether the user found the response helpful. */
  wasHelpful: boolean;
  /** Numeric rating (e.g. 1-5). */
  rating: number;
  /** Optional free-text feedback from the user. */
  feedbackText?: string;
  /** "positive", "negative", or "need_more_detail". */
  feedbackType?: string;
}

/**
 * Hook for submitting per-response user feedback to the backend.
 *
 * Wraps the `POST /feedback` API call and binds the current
 * `sessionId` from the chat store automatically.
 *
 * @returns `submitFeedback` - resolves to `true` on success, `false` on error.
 */
export function useFeedback() {
  const { sessionId } = useChatStore();

  const submitFeedback = useCallback(async (params: FeedbackParams) => {
    try {
      await api.submitFeedback({
        session_id: sessionId,
        query: params.query,
        response_id: params.messageId,
        rating: params.rating,
        feedback_text: params.feedbackText,
        was_helpful: params.wasHelpful,
        feedback_type: params.feedbackType,
      });
      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return false;
    }
  }, [sessionId]);

  return {
    submitFeedback,
  };
}
